/**
 * 设计令牌 (Design Tokens)。
 *
 * 之前相同的数值（颜色 #d97d83、间距 42px、字体大小 0.875rem、不透明度 0.75）
 * 被分别复制到了 7 个组件中。修改其中一处时，其余部分仍保持不变的结构。
 */

const color = {
  background: "#efebe9",
  surface: "#ffffff",
  overlay: "rgba(0, 0, 0, 0.45)",

  /**
   * 强调色。原有的值 #d97d83 与背景 #efebe9 的对比度为 2.46:1，
   * 未达到 WCAG AA 正文标准 (4.5:1) 以及大字体标准 (3:1)。
   * 在保持相同色调的同时降低亮度，确保获得了 4.97:1 的对比度。
   */
  accent: "#a8434a",
  /** 仅用于非文本装饰（分割线、边框、背景）的浅色。 */
  accentSoft: "#d97d83",
  accentSurface: "rgba(168, 67, 74, 0.08)",

  /**
   * 正文颜色。之前通过 opacity 调节色调，但 opacity 会导致子元素也变模糊，
   * 且重叠时的对比度变得不可预测。现在直接使用颜色值表示。
   */
  text: "rgba(0, 0, 0, 0.82)",
  textMuted: "rgba(0, 0, 0, 0.62)",
  textSubtle: "rgba(0, 0, 0, 0.45)",
  border: "rgba(0, 0, 0, 0.12)",

  focusRing: "#1a73e8",
} as const;

const font = {
  body: '"Noto Serif SC", "Songti SC", STSong, SimSun, serif',
  /** 标题与引用使用中文楷体，并保留宋体回退。 */
  display: 'STKaiti, KaiTi, "Noto Serif SC", "Songti SC", serif',
} as const;

const fontSize = {
  xs: "0.75rem",
  sm: "0.875rem",
  md: "1rem",
  lg: "1.25rem",
  xl: "1.5rem",
  display: "1.375rem",
} as const;

/** 4px 倍数比例。原有的 42px 已标准化为 40px (space.10)。 */
const space = {
  1: "4px",
  2: "8px",
  3: "12px",
  4: "16px",
  6: "24px",
  8: "32px",
  10: "40px",
  12: "48px",
  16: "64px",
} as const;

const layout = {
  /**
   * 虽然是移动端婚礼邀请函，但也能在桌面端打开。之前仅指定 width: 70%，
   * 导致在宽屏上单行文字过长。
   */
  maxWidth: "480px",
  contentInset: "clamp(20px, 8vw, 48px)",
} as const;

const motion = {
  duration: { fast: "150ms", base: "300ms", slow: "900ms" },
  easing: "cubic-bezier(0.22, 1, 0.36, 1)",
} as const;

const zIndex = {
  /* 悬浮在正文上方的控件（如背景音乐切换等） */
  floating: 50,
  modal: 100,
  toast: 200,
} as const;

export const theme = {
  color,
  font,
  fontSize,
  space,
  layout,
  motion,
  zIndex,
} as const;

export type Theme = typeof theme;
